import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  MenuItem,
  IconButton,
  Divider,
  Grid,
  Paper,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface Item {
  codigo: string;
  descripcion: string;
  cantidad: number;
  unidadMedida: string;
  precioUnitario: number;
  afectacionIGV: string;
  igv: number;
  total: number;
}

export default function CrearComprobante() {
  const navigate = useNavigate();
  const { isOperador, empresasAsignadas } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [empresas, setEmpresas] = useState<any[]>([]);
  const [series, setSeries] = useState<any[]>([]);

  const [formData, setFormData] = useState({
    empresaRuc: '',
    tipo: '03', // Boleta por defecto
    serieSeleccionada: '',
    receptor: {
      tipoDocumento: '1', // DNI por defecto
      numeroDocumento: '',
      nombre: '',
      razonSocial: '',
      direccion: '',
    },
    items: [] as Item[],
    moneda: 'PEN',
    observaciones: '',
    // Campos de detracción
    aplicaDetraccion: false,
    codigoDetraccion: '',
    porcentajeDetraccion: '',
  });

  const [subtotal, setSubtotal] = useState(0);
  const [totalIGV, setTotalIGV] = useState(0);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadEmpresas();
  }, []);

  useEffect(() => {
    if (formData.empresaRuc && formData.tipo) {
      loadSeries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.empresaRuc, formData.tipo]);

  useEffect(() => {
    calcularTotales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.items]);

  const loadEmpresas = async () => {
    try {
      const data = await apiService.getEmpresas();
      let empresasList = data.empresas || [];
      
      // Si es operador, filtrar solo las empresas asignadas
      if (isOperador && empresasAsignadas.length > 0) {
        empresasList = empresasList.filter((empresa: any) => 
          empresasAsignadas.includes(empresa.ruc)
        );
      }
      
      setEmpresas(empresasList);
      
      // Seleccionar automáticamente la primera empresa
      if (empresasList.length > 0) {
        setFormData(prev => ({ ...prev, empresaRuc: empresasList[0].ruc }));
      }
    } catch (err) {
      console.error('Error al cargar empresas:', err);
    }
  };

  const loadSeries = async () => {
    try {
      const data = await apiService.getSeries(formData.empresaRuc);
      const seriesFiltradas = data.series.filter(
        (s: any) => s.tipoComprobante === formData.tipo && s.activo
      );
      setSeries(seriesFiltradas);
      
      // Seleccionar la primera serie activa automáticamente
      if (seriesFiltradas.length > 0) {
        setFormData(prev => ({ ...prev, serieSeleccionada: seriesFiltradas[0].serie }));
      }
    } catch (err) {
      console.error('Error al cargar series:', err);
      setSeries([]);
    }
  };

  const calcularTotales = () => {
    let totalGravadas = 0;
    let totalIgv = 0;
    
    formData.items.forEach(item => {
      totalGravadas += item.total;
      totalIgv += item.igv;
    });

    const total = totalGravadas + totalIgv;

    setSubtotal(totalGravadas);
    setTotalIGV(totalIgv);
    setTotal(total);
  };

  const agregarItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          codigo: '',
          descripcion: '',
          cantidad: 1,
          unidadMedida: 'NIU',
          precioUnitario: 0,
          afectacionIGV: '10', // Gravado
          igv: 0,
          total: 0,
        },
      ],
    }));
  };

  const eliminarItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const actualizarItem = (index: number, campo: string, valor: any) => {
    const nuevosItems = [...formData.items];
    nuevosItems[index] = { ...nuevosItems[index], [campo]: valor };

    // Calcular IGV y total del item
    const item = nuevosItems[index];
    const baseImponible = item.cantidad * item.precioUnitario;
    
    if (item.afectacionIGV === '10') {
      // Gravado - calcular IGV del item
      item.igv = baseImponible * 0.18;
      item.total = baseImponible;
    } else {
      // Exonerado, Inafecto, etc.
      item.igv = 0;
      item.total = baseImponible;
    }

    setFormData(prev => ({ ...prev, items: nuevosItems }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.items.length === 0) {
      setError('Debe agregar al menos un item');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Preparar datos según el tipo de comprobante
      const datos: any = {
        items: formData.items,
        subtotal,
        igv: totalIGV,
        total,
        moneda: formData.moneda,
        observaciones: formData.observaciones || undefined,
      };

      if (formData.tipo === '03') {
        // Boleta
        datos.receptor = {
          tipoDocumento: formData.receptor.tipoDocumento,
          numeroDocumento: formData.receptor.numeroDocumento,
          nombre: formData.receptor.nombre,
        };
      } else {
        // Factura
        datos.receptor = {
          ruc: formData.receptor.numeroDocumento,
          razonSocial: formData.receptor.razonSocial,
          direccion: formData.receptor.direccion ? {
            direccion: formData.receptor.direccion,
            ubigeo: '150101',
            departamento: 'LIMA',
            provincia: 'LIMA',
            distrito: 'LIMA',
          } : undefined,
        };

        // Agregar detracción si aplica
        if (formData.aplicaDetraccion) {
          const porcentaje = parseFloat(formData.porcentajeDetraccion) || 0;
          datos.detraccion = {
            aplicaDetraccion: true,
            codigoDetraccion: formData.codigoDetraccion,
            porcentaje,
            montoDetraccion: total * (porcentaje / 100),
          };
        }
      }

      const response = await apiService.crearComprobante(
        formData.empresaRuc,
        formData.tipo,
        datos
      );

      setSuccess(`Comprobante ${response.data.numeroComprobante} creado exitosamente`);
      
      // Redirigir a la lista de comprobantes después de 2 segundos
      setTimeout(() => {
        navigate('/comprobantes');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.mensaje || 'Error al crear comprobante');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Crear Comprobante
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Generar nueva boleta o factura electrónica
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Datos del Comprobante
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Empresa"
                  value={formData.empresaRuc}
                  onChange={(e) => setFormData({ ...formData, empresaRuc: e.target.value })}
                  required
                >
                  <MenuItem value="">Seleccione una empresa</MenuItem>
                  {empresas.map((empresa) => (
                    <MenuItem key={empresa.ruc} value={empresa.ruc}>
                      {empresa.ruc} - {empresa.razonSocial}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Tipo de Comprobante"
                  value={formData.tipo}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                  required
                >
                  <MenuItem value="03">Boleta</MenuItem>
                  <MenuItem value="01">Factura</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Serie"
                  value={formData.serieSeleccionada}
                  onChange={(e) => setFormData({ ...formData, serieSeleccionada: e.target.value })}
                  required
                  disabled={!formData.empresaRuc || series.length === 0}
                  helperText={series.length === 0 && formData.empresaRuc ? 'No hay series activas' : ''}
                >
                  {series.map((s) => (
                    <MenuItem key={s.serie} value={s.serie}>
                      {s.serie} (Siguiente: {String(s.correlativo + 1).padStart(8, '0')})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Datos del Receptor
            </Typography>

            <Grid container spacing={2}>
              {formData.tipo === '03' ? (
                <>
                  <Grid item xs={12} md={3}>
                    <TextField
                      select
                      fullWidth
                      label="Tipo Documento"
                      value={formData.receptor.tipoDocumento}
                      onChange={(e) => setFormData({
                        ...formData,
                        receptor: { ...formData.receptor, tipoDocumento: e.target.value }
                      })}
                      required
                    >
                      <MenuItem value="1">DNI</MenuItem>
                      <MenuItem value="4">Carnet de Extranjería</MenuItem>
                      <MenuItem value="7">Pasaporte</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Número de Documento"
                      value={formData.receptor.numeroDocumento}
                      onChange={(e) => setFormData({
                        ...formData,
                        receptor: { ...formData.receptor, numeroDocumento: e.target.value }
                      })}
                      required
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Nombre Completo"
                      value={formData.receptor.nombre}
                      onChange={(e) => setFormData({
                        ...formData,
                        receptor: { ...formData.receptor, nombre: e.target.value }
                      })}
                      required
                    />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="RUC"
                      value={formData.receptor.numeroDocumento}
                      onChange={(e) => setFormData({
                        ...formData,
                        receptor: { ...formData.receptor, numeroDocumento: e.target.value }
                      })}
                      required
                      inputProps={{ maxLength: 11 }}
                    />
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      label="Razón Social"
                      value={formData.receptor.razonSocial}
                      onChange={(e) => setFormData({
                        ...formData,
                        receptor: { ...formData.receptor, razonSocial: e.target.value }
                      })}
                      required
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Dirección"
                      value={formData.receptor.direccion}
                      onChange={(e) => setFormData({
                        ...formData,
                        receptor: { ...formData.receptor, direccion: e.target.value }
                      })}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>

        {formData.tipo === '01' && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Detracción (Opcional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Sistema de Pago de Obligaciones Tributarias (SPOT)
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="¿Aplica Detracción?"
                    value={formData.aplicaDetraccion ? 'true' : 'false'}
                    onChange={(e) => setFormData({ ...formData, aplicaDetraccion: e.target.value === 'true' })}
                    SelectProps={{ native: true }}
                  >
                    <option value="false">No</option>
                    <option value="true">Sí</option>
                  </TextField>
                </Grid>

                {formData.aplicaDetraccion && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        fullWidth
                        label="Código de Detracción"
                        value={formData.codigoDetraccion}
                        onChange={(e) => setFormData({ ...formData, codigoDetraccion: e.target.value })}
                        required
                        SelectProps={{ native: true }}
                        helperText="Catálogo 54 - SUNAT"
                      >
                        <option value="">Seleccione un código</option>
                        <option value="001">001 - Azúcar</option>
                        <option value="003">003 - Alcohol etílico</option>
                        <option value="004">004 - Recursos hidrobiológicos</option>
                        <option value="005">005 - Maíz amarillo duro</option>
                        <option value="006">006 - Algodón</option>
                        <option value="007">007 - Caña de azúcar</option>
                        <option value="008">008 - Madera</option>
                        <option value="009">009 - Arena y piedra</option>
                        <option value="010">010 - Residuos, subproductos, desechos</option>
                        <option value="011">011 - Bienes del inciso A) del Apéndice I de la Ley del IGV</option>
                        <option value="012">012 - Intermediación laboral y tercerización</option>
                        <option value="014">014 - Carnes y despojos comestibles</option>
                        <option value="017">017 - Embarcaciones pesqueras</option>
                        <option value="019">019 - Arrendamiento de bienes muebles</option>
                        <option value="020">020 - Mantenimiento y reparación de bienes muebles</option>
                        <option value="021">021 - Movimiento de carga</option>
                        <option value="022">022 - Otros servicios empresariales</option>
                        <option value="023">023 - Leche</option>
                        <option value="024">024 - Comisión mercantil</option>
                        <option value="025">025 - Fabricación de bienes por encargo</option>
                        <option value="026">026 - Servicio de transporte de personas</option>
                        <option value="027">027 - Transporte de bienes por vía terrestre</option>
                        <option value="030">030 - Contratos de construcción</option>
                        <option value="031">031 - Oro gravado con el IGV</option>
                        <option value="032">032 - Páprika y otros frutos de los géneros capsicum o pimienta</option>
                        <option value="034">034 - Minerales metálicos no auríferos</option>
                        <option value="035">035 - Bienes exonerados del IGV</option>
                        <option value="036">036 - Oro y demás minerales metálicos exonerados del IGV</option>
                        <option value="037">037 - Demás servicios gravados con el IGV</option>
                        <option value="039">039 - Minerales no metálicos</option>
                        <option value="040">040 - Bien inmueble gravado con el IGV</option>
                      </TextField>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Porcentaje de Detracción (%)"
                        type="number"
                        value={formData.porcentajeDetraccion}
                        onChange={(e) => setFormData({ ...formData, porcentajeDetraccion: e.target.value })}
                        required
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                        helperText={`Monto: ${formData.moneda} ${(total * (parseFloat(formData.porcentajeDetraccion) || 0) / 100).toFixed(2)}`}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Alert severity="info">
                        La detracción se calculará automáticamente sobre el total de la factura. Asegúrese de que la empresa tenga configurada la cuenta del Banco de la Nación.
                      </Alert>
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Items
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={agregarItem}
                size="small"
              >
                Agregar Item
              </Button>
            </Box>

            {formData.items.length === 0 ? (
              <Alert severity="info">
                No hay items agregados. Haga clic en "Agregar Item" para comenzar.
              </Alert>
            ) : (
              formData.items.map((item, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2">Item {index + 1}</Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => eliminarItem(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        label="Código"
                        value={item.codigo}
                        onChange={(e) => actualizarItem(index, 'codigo', e.target.value)}
                        required
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Descripción"
                        value={item.descripcion}
                        onChange={(e) => actualizarItem(index, 'descripcion', e.target.value)}
                        required
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        label="Cantidad"
                        type="number"
                        value={item.cantidad}
                        onChange={(e) => actualizarItem(index, 'cantidad', parseFloat(e.target.value) || 0)}
                        required
                        size="small"
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <TextField
                        select
                        fullWidth
                        label="Unidad"
                        value={item.unidadMedida}
                        onChange={(e) => actualizarItem(index, 'unidadMedida', e.target.value)}
                        required
                        size="small"
                      >
                        <MenuItem value="NIU">Unidad</MenuItem>
                        <MenuItem value="ZZ">Servicio</MenuItem>
                        <MenuItem value="KGM">Kilogramo</MenuItem>
                        <MenuItem value="MTR">Metro</MenuItem>
                      </TextField>
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        label="Precio Unit."
                        type="number"
                        value={item.precioUnitario}
                        onChange={(e) => actualizarItem(index, 'precioUnitario', parseFloat(e.target.value) || 0)}
                        required
                        size="small"
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <TextField
                        select
                        fullWidth
                        label="Afectación IGV"
                        value={item.afectacionIGV}
                        onChange={(e) => actualizarItem(index, 'afectacionIGV', e.target.value)}
                        required
                        size="small"
                      >
                        <MenuItem value="10">Gravado</MenuItem>
                        <MenuItem value="20">Exonerado</MenuItem>
                        <MenuItem value="30">Inafecto</MenuItem>
                      </TextField>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="IGV"
                        value={item.igv.toFixed(2)}
                        size="small"
                        disabled
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Subtotal"
                        value={item.total.toFixed(2)}
                        size="small"
                        disabled
                      />
                    </Grid>
                  </Grid>
                </Paper>
              ))
            )}

            {formData.items.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Box sx={{ width: 300 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>Op. Gravadas:</Typography>
                      <Typography>{formData.moneda} {subtotal.toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>I.G.V. (18%):</Typography>
                      <Typography>{formData.moneda} {totalIGV.toFixed(2)}</Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="h6">TOTAL:</Typography>
                      <Typography variant="h6">{formData.moneda} {total.toFixed(2)}</Typography>
                    </Box>
                  </Box>
                </Box>
              </>
            )}
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Moneda"
                  value={formData.moneda}
                  onChange={(e) => setFormData({ ...formData, moneda: e.target.value })}
                  required
                >
                  <MenuItem value="PEN">Soles (PEN)</MenuItem>
                  <MenuItem value="USD">Dólares (USD)</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Observaciones"
                  value={formData.observaciones}
                  onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                  multiline
                  rows={1}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading || formData.items.length === 0}
          >
            {loading ? 'Creando...' : 'Crear Comprobante'}
          </Button>

          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/comprobantes')}
            disabled={loading}
          >
            Cancelar
          </Button>
        </Box>
      </form>
    </Box>
  );
}
